import { yupResolver } from '@hookform/resolvers/yup';
import { IRegistrationFormValuesFormModal } from 'components/universal/form-modal';
import { PATHS } from 'constants/routes';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchDepartmentsPaginated, getEmployeePositions, getWorkplaces, signUp } from 'services/registration';
import { showModal } from 'store/modal/modalSlice';
import { SignUpDTO } from 'types/user';
import useDebounce from 'utils/useDebounce';
import { schema } from './validation';

export interface PaginatedResponse<T> {
    current_page: number;
    data: T[];
    last_page: number;
}

export interface IRegistrationFormValues {
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    password: string;
    password_repeat: string;
    workplace_name: string;
    employee_position_id: number | null;
    workplace_address_street: string;
    workplace_address_number: string;
    workplace_address_city: string;
    workplace_address_postcode: string;
    agreement: boolean;
    department_id?: number | null;
}

export interface WorkplaceType {
    id?: number;
    workplace_name: string;
    workplace_address_city: string;
    workplace_address_street: string;
    workplace_address_number: string;
    workplace_address_postcode: string;
}

const defaultValues: IRegistrationFormValues = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '000000000',
    password: '',
    password_repeat: '',
    workplace_name: '',
    employee_position_id: null,
    workplace_address_street: '',
    workplace_address_number: '',
    workplace_address_city: '',
    workplace_address_postcode: '',
    agreement: false,
};

export interface Department {
    id: number;
    name: string;
    workplace_id: number;
}

export const useRegistrationHook = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordSecond, setShowPasswordSecond] = useState(false);
    const [localOptions, setLocalOptions] = useState<WorkplaceType[]>([]);
    const [postcode, setPostcode] = useState('');
    const [city, setCity] = useState('');
    const [workplaceId, setWorkplaceId] = useState(0);
    const [selectedWorkplace, setSelectedWorkplace] = useState<{ label: string; value: WorkplaceType } | null>(null);
    const [selectedDepartment, setSelectedDepartment] = useState<{ label: string; value: number } | null>(null);
    const [selectedPosition, setSelectedPosition] = useState<{ label: string; value: number } | null>(null);
    const [searchName, setSearchName] = useState('');
    const debouncedSearch = useDebounce(postcode, 200);
    const debouncedSearchCity = useDebounce(city, 200);
    const debouncedSearchName = useDebounce(searchName, 200);

    const {
        control,
        register,
        handleSubmit,
        setError,
        watch,
        formState: { errors },
        setValue,
    } = useForm<IRegistrationFormValues>({
        resolver: yupResolver(schema),
        defaultValues,
    });

    const signUpMutation = useMutation((data: SignUpDTO) => signUp(data), {
        onSuccess: (data) => {
            toast.success(data?.data?.message);
            navigate(PATHS.CONFIRM_EMAIL_ADDRESS, {
                state: { email: watch('email') },
            });
        },
        onError: (error: any) => {
            const formErrors = error?.response?.data?.errors;
            if (formErrors) {
                Object.entries(formErrors).forEach(([key, value]) => {
                    setError(key as keyof IRegistrationFormValues, {
                        message: value?.[0],
                    });
                });
            }
        },
    });

    const onSubmit = (formData: IRegistrationFormValues) => {
        if (workplaceId !== 0) {
            formData['workplace_id'] = workplaceId;
        }

        signUpMutation.mutate(formData);
    };

    const convertNewWorkplaceToOption = (
        workplace: IRegistrationFormValuesFormModal
    ): WorkplaceType => ({
        workplace_name: workplace.workplace_name,
        workplace_address_postcode: workplace.workplace_address_postcode,
        workplace_address_city: workplace.workplace_address_city,
        workplace_address_street: workplace.workplace_address_street,
        workplace_address_number: workplace.workplace_address_number,
    });

    const setValueForm = (newWorkplace: IRegistrationFormValuesFormModal) => {
        const option = convertNewWorkplaceToOption(newWorkplace);
        setLocalOptions((prev) => [...prev, option]);
        setSelectedWorkplace({ label: newWorkplace.workplace_name, value: option });
        setValue('workplace_address_street', newWorkplace.workplace_address_street);
        setValue('workplace_address_number', newWorkplace.workplace_address_number);
        setValue('workplace_address_city', newWorkplace.workplace_address_city);
        setValue('workplace_address_postcode', newWorkplace.workplace_address_postcode);
        setValue('workplace_name', newWorkplace.workplace_name);
    };

    const onKeyUpHandle = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const zipCode = (event.target as HTMLInputElement).value;
        setPostcode(zipCode);
    };

    const loadWorkplaceOptions = async (search: string, _: any, { page }: { page: number }) => {
        const res = await getWorkplaces({ city: city || undefined, name: search || undefined, postcode: postcode || undefined, page });
        return {
            options: res.data.map((w: WorkplaceType) => ({ label: w.workplace_name, value: w })),
            hasMore: res.current_page < res.last_page,
            additional: { page: page + 1 },
        };
    };

    const handleWorkplaceChange = (option: { label: string; value: WorkplaceType } | null) => {
        setSelectedWorkplace(option);
        setSelectedDepartment(null);
        setValue('department_id', null);
        if (option?.value) {
            const w = option.value;
            setWorkplaceId(w.id ?? 0);
            setValue('workplace_name', w.workplace_name);
            setValue('workplace_address_street', w.workplace_address_street);
            setValue('workplace_address_number', w.workplace_address_number);
            setValue('workplace_address_city', w.workplace_address_city);
            setValue('workplace_address_postcode', w.workplace_address_postcode);
        } else {
            setWorkplaceId(0);
        }
    };

    const loadDepartmentOptions = async (search: string, _: any, { page }: { page: number }) => {
        if (!workplaceId) return { options: [], hasMore: false, additional: { page: 1 } };
        const res = await fetchDepartmentsPaginated(workplaceId, page);
        return {
            options: res.data.map((d: any) => ({ label: d.name, value: d.id })),
            hasMore: res.current_page < res.last_page,
            additional: { page: page + 1 },
        };
    };

    const handleDepartmentChange = (option: { label: string; value: number } | null, fieldOnChange: (val: number | null) => void) => {
        setSelectedDepartment(option);
        fieldOnChange(option?.value ?? null);
    };

    const loadPositionOptions = async (search: string, _: any, { page }: { page: number }) => {
        const res = await getEmployeePositions(page, search || undefined);
        return {
            options: res.data.map((p) => ({ label: p.name, value: p.id })),
            hasMore: res.current_page < res.last_page,
            additional: { page: page + 1 },
        };
    };

    const handlePositionChange = (option: { label: string; value: number } | null, fieldOnChange: (val: number | null) => void) => {
        setSelectedPosition(option);
        fieldOnChange(option?.value ?? null);
    };

    const onChangeCity = (city) => {
        setCity(city.value);
        setSearchName('');
    };

    const formatZipCode = (e) => {
        let value = e.target.value.replace(/\D/g, '');
        if (value.length > 5) value = value.slice(0, 5);
        if (value.length > 2) value = `${value.slice(0, 2)}-${value.slice(2)}`;
        e.target.value = value;
    };

    const togglePassword = () => setShowPassword((prev) => !prev);
    const togglePasswordRepeat = () => setShowPasswordSecond((prev) => !prev);
    const openModal = () => dispatch(showModal());
    const goBack = () => navigate(PATHS.REGISTER_CHOOSE);
    
    return {
        register,
        control,
        handleSubmit,
        onSubmit,
        errors,
        setValue,
        t,
        showPassword,
        showPasswordSecond,
        togglePassword,
        togglePasswordRepeat,
        onKeyUpHandle,
        setValueForm,
        openModal,
        goBack,
        watch,
        setWorkplaceId,
        debouncedSearch,
        postcode,
        onChangeCity,
        debouncedSearchCity,
        debouncedSearchName,
        setSearchName,
        searchName,
        formatZipCode,
        selectedWorkplace,
        loadWorkplaceOptions,
        handleWorkplaceChange,
        selectedDepartment,
        loadDepartmentOptions,
        handleDepartmentChange,
        workplaceId,
        selectedPosition,
        loadPositionOptions,
        handlePositionChange,
    };
};
